import { useState, useEffect, useMemo } from 'react';
import { Building2, Pencil, UserPlus, UserMinus, FileText, ArrowLeft, ExternalLink, Bot, Plus, Trash2, UserX, Megaphone, MessageSquare } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AIInput } from '@/components/ui/ai-input';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn, formatCurrency } from '@/lib/utils';
import { mergeModelOptionsForUiProvider, type AiModelSuggestions } from '@/lib/ai-model-suggestions';
import type { Supplier, SupplierBudget, Campaign, Document, SupplierCompanionBudgetRow, User, ApiResponse } from '@/types';
import { api } from '@/lib/api';
import { withReturnTo } from '@/lib/return-path';

const COMPANION_PROVIDERS: { value: 'openai' | 'azure' | 'modelrouter' | 'ollama'; label: string }[] = [
  { value: 'openai', label: 'OpenAI' },
  { value: 'azure', label: 'Azure OpenAI' },
  { value: 'modelrouter', label: 'Model Router' },
  { value: 'ollama', label: 'Ollama' },
];

/** Same presets as KI-Assistants → Agent; models are not fetched from the provider API — stored as plain text on the agent. */
const COMPANION_MODEL_PRESETS = ['auto', 'gpt-4o', 'gpt-4o-mini', 'gpt-4.1', 'gpt-4.1-mini', 'o4-mini', 'o3-mini'] as const;
const COMPANION_MODEL_FIELD_HINT =
  'Vorschläge stammen aus den unter Einstellungen hinterlegten Provider-Credentials, KI-Defaults und bestehenden Agenten — keine Live-Abfrage beim Provider. Der Wert wird gespeichert und bei Aufrufen verwendet. Freie Eingabe bleibt möglich. Azure: Deployment-Name exakt wie in Azure.';

type SupplierDetail = Supplier & {
  campaigns_count?: number;
  access?: { user_id: string; users: { id: string; full_name: string; email: string } | null }[];
};

interface SupplierDetailPageProps {
  supplierId: string;
  onNavigate: (path: string) => void;
  onBack?: () => void;
}

export function SupplierDetailPage({ supplierId, onNavigate, onBack }: SupplierDetailPageProps) {
  const [supplier, setSupplier] = useState<(Supplier & { campaigns_count?: number }) | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailBudgets, setDetailBudgets] = useState<SupplierBudget[]>([]);
  const [detailCampaigns, setDetailCampaigns] = useState<Campaign[]>([]);
  const [detailDocs, setDetailDocs] = useState<Document[]>([]);
  const [accessUsers, setAccessUsers] = useState<{ id: string; email: string; name: string }[]>([]);
  const [accessDialogOpen, setAccessDialogOpen] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [accessUserId, setAccessUserId] = useState('');
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessBusy, setAccessBusy] = useState(false);
  const [accessError, setAccessError] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', company: '', contact_name: '', contact_email: '', phone: '', notes: '' });
  const [companions, setCompanions] = useState<SupplierCompanionBudgetRow[]>([]);
  const [companionDialogOpen, setCompanionDialogOpen] = useState(false);
  const [newCompanion, setNewCompanion] = useState({
    name: '',
    provider: 'openai' as 'openai' | 'azure' | 'modelrouter' | 'ollama',
    model: 'gpt-4o',
    year: new Date().getFullYear(),
    allocated_budget: '',
  });
  const [allocDrafts, setAllocDrafts] = useState<Record<string, string>>({});
  const [companionBusy, setCompanionBusy] = useState(false);
  const [supplierTab, setSupplierTab] = useState('info');
  const [modelSuggestions, setModelSuggestions] = useState<AiModelSuggestions | null>(null);

  const currentYear = new Date().getFullYear();

  const companionSumForYear = useMemo(() => {
    return companions
      .filter((c) => c.year === currentYear)
      .reduce((s, c) => s + (Number(c.allocated_budget) || 0), 0);
  }, [companions, currentYear]);

  const modelQuickPickOptions = useMemo(
    () => mergeModelOptionsForUiProvider(newCompanion.provider, modelSuggestions, COMPANION_MODEL_PRESETS),
    [newCompanion.provider, modelSuggestions]
  );

  useEffect(() => {
    if (!companionDialogOpen) return;
    void api
      .get<{ success?: boolean; data?: AiModelSuggestions }>('/ai/model-suggestions')
      .then((res) => {
        if (res.success !== false && res.data) setModelSuggestions(res.data);
        else setModelSuggestions(null);
      })
      .catch(() => setModelSuggestions(null));
  }, [companionDialogOpen]);

  useEffect(() => {
    setSupplierTab('info');
  }, [supplierId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSupplier(null);
    setDetailBudgets([]);
    setAccessUsers([]);
    api
      .get<ApiResponse<SupplierDetail>>(`/suppliers/${supplierId}`)
      .then((res) => {
        if (!res.success || !res.data) throw new Error(res.error || 'Lieferant konnte nicht geladen werden.');
        if (cancelled) return;
        setSupplier(res.data);
        setDetailBudgets(res.data.budgets ?? []);
        setAccessUsers((res.data.access ?? []).flatMap((row) => row.users
          ? [{ id: row.user_id, name: row.users.full_name, email: row.users.email }]
          : []));
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Lieferant konnte nicht geladen werden.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [supplierId]);

  useEffect(() => {
    let cancelled = false;
    setDetailCampaigns([]);
    setDetailDocs([]);
    setCompanions([]);
    const loadRelated = async <T,>(path: string, update: (rows: T[]) => void) => {
      try {
        const res = await api.get<ApiResponse<T[]>>(path);
        if (!res.success || !Array.isArray(res.data)) throw new Error(res.error || 'Lieferantendetails konnten nicht geladen werden.');
        if (!cancelled) update(res.data);
      } catch (e) {
        if (!cancelled) setError((previous) => previous || (e instanceof Error ? e.message : 'Lieferantendetails konnten nicht geladen werden.'));
      }
    };
    void loadRelated<Campaign>(`/suppliers/${supplierId}/campaigns`, setDetailCampaigns);
    void loadRelated<Document>(`/documents?entity_type=supplier&entity_id=${encodeURIComponent(supplierId)}&limit=100`, setDetailDocs);
    void loadRelated<SupplierCompanionBudgetRow>(`/suppliers/${supplierId}/companions`, setCompanions);
    return () => { cancelled = true; };
  }, [supplierId]);

  useEffect(() => {
    const next: Record<string, string> = {};
    for (const c of companions) {
      next[c.id] = String(c.allocated_budget ?? 0);
    }
    setAllocDrafts(next);
  }, [companions]);

  const refreshSupplierFromApi = async () => {
    try {
      const res = await api.get<{ success?: boolean; data?: Supplier }>(`/suppliers/${supplierId}`);
      const data = (res as { data?: Supplier }).data;
      if (data) setSupplier(data as Supplier & { campaigns_count?: number });
    } catch {}
  };

  const deactivateSupplier = async () => {
    if (!supplier?.is_active) return;
    if (
      !window.confirm(
        `Lieferant «${supplier.name}» wirklich deaktivieren?\n\n` +
          'Alle zugehörigen Aktivitäten, Kampagnen und Jahresbudgets werden inaktiv geschaltet. ' +
          'Verknüpfte Marketingstrategien (optional) werden archiviert. Kennzahlen berücksichtigen diesen Partner nicht mehr.'
      )
    ) {
      return;
    }
    setError(null);
    try {
      const res = await api.patch<{ success?: boolean; error?: string }>(`/suppliers/${supplierId}/deactivate`, {});
      if (res.success === false) {
        setError(res.error ?? 'Deaktivieren fehlgeschlagen.');
        return;
      }
      await refreshSupplierFromApi();
      void api.get<{ data?: Campaign[] }>(`/suppliers/${supplierId}/campaigns`).then((r: any) =>
        setDetailCampaigns((r as { data?: Campaign[] }).data ?? [])
      );
    } catch (e) {
      setError((e as Error).message ?? 'Deaktivieren fehlgeschlagen.');
    }
  };

  const createCompanion = async () => {
    if (!newCompanion.name.trim() || companionBusy) return;
    setCompanionBusy(true);
    setError(null);
    try {
      const allocated = Number.parseFloat(newCompanion.allocated_budget.replace("'", '').replace(',', '.')) || 0;
      const res = await api.post<{ success?: boolean; data?: SupplierCompanionBudgetRow; error?: string }>(
        `/suppliers/${supplierId}/companions`,
        {
          name: newCompanion.name.trim(),
          provider: newCompanion.provider,
          model: newCompanion.model.trim() || 'gpt-4o',
          year: newCompanion.year,
          allocated_budget: allocated,
        }
      );
      if (res.success === false || !res.data) {
        setError(res.error ?? 'Companion konnte nicht angelegt werden.');
        return;
      }
      setCompanions((prev) => [res.data!, ...prev]);
      await refreshSupplierFromApi();
      setCompanionDialogOpen(false);
      setSupplierTab('companions');
      setNewCompanion({
        name: '',
        provider: 'openai',
        model: 'gpt-4o',
        year: currentYear,
        allocated_budget: '',
      });
    } catch (e) {
      setError((e as Error).message ?? 'Companion konnte nicht angelegt werden.');
    } finally {
      setCompanionBusy(false);
    }
  };

  const saveCompanionAllocation = async (row: SupplierCompanionBudgetRow) => {
    const raw = allocDrafts[row.id] ?? String(row.allocated_budget);
    const allocated = Number.parseFloat(String(raw).replace("'", '').replace(',', '.')) || 0;
    if (allocated === Number(row.allocated_budget)) return;
    setCompanionBusy(true);
    setError(null);
    try {
      const res = await api.patch<{ success?: boolean; data?: SupplierCompanionBudgetRow; error?: string }>(
        `/suppliers/${supplierId}/companion-budgets/${row.id}`,
        { allocated_budget: allocated }
      );
      if (res.success === false || !res.data) {
        setError(res.error ?? 'Zuteilung konnte nicht gespeichert werden.');
        return;
      }
      setCompanions((prev) => prev.map((c) => (c.id === row.id ? res.data! : c)));
      await refreshSupplierFromApi();
    } catch (e) {
      setError((e as Error).message ?? 'Zuteilung konnte nicht gespeichert werden.');
    } finally {
      setCompanionBusy(false);
    }
  };

  const openCompanionDialog = () => {
    setError(null);
    setNewCompanion({
      name: '',
      provider: 'openai',
      model: 'gpt-4o',
      year: currentYear,
      allocated_budget: '',
    });
    setCompanionDialogOpen(true);
  };

  const removeCompanion = async (agentId: string) => {
    if (!window.confirm('Companion von diesem Lieferanten entfernen? Der KI-Agent bleibt unter Companion verfügbar.')) return;
    setCompanionBusy(true);
    setError(null);
    try {
      const res = await api.delete<{ success?: boolean; error?: string }>(`/suppliers/${supplierId}/companions/${agentId}`);
      if (res.success === false) {
        setError(res.error ?? 'Entfernen fehlgeschlagen.');
        return;
      }
      setCompanions((prev) => prev.filter((c) => c.ai_agent_id !== agentId));
      await refreshSupplierFromApi();
    } catch (e) {
      setError((e as Error).message ?? 'Entfernen fehlgeschlagen.');
    } finally {
      setCompanionBusy(false);
    }
  };

  const openEdit = () => {
    if (!supplier) return;
    setForm({
      name: supplier.name,
      company: supplier.company ?? '',
      contact_name: supplier.contact_name ?? '',
      contact_email: supplier.contact_email ?? '',
      phone: supplier.phone ?? '',
      notes: supplier.notes ?? '',
    });
    setDialogOpen(true);
  };

  const saveSupplier = async () => {
    try {
      await api.patch(`/suppliers/${supplierId}`, { ...form, name: form.name.trim(), contact_email: form.contact_email.trim() || null });
      setSupplier((prev) => prev ? { ...prev, ...form } : prev);
      setDialogOpen(false);
    } catch (e) {
      setError((e as Error).message ?? 'Speichern fehlgeschlagen.');
    }
  };

  const revokeAccess = async (userId: string) => {
    if (accessBusy) return;
    setAccessBusy(true);
    setError(null);
    try {
      const result = await api.delete<ApiResponse<unknown>>(`/suppliers/${supplierId}/access/${userId}`);
      if (!result.success) throw new Error(result.error || 'Zugriff konnte nicht entzogen werden.');
      setAccessUsers((prev) => prev.filter((u) => u.id !== userId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Zugriff konnte nicht entzogen werden.');
    } finally {
      setAccessBusy(false);
    }
  };

  const openAccessDialog = async () => {
    setAccessDialogOpen(true);
    setAccessUserId('');
    setAccessError(null);
    setAvailableUsers([]);
    setAccessLoading(true);
    try {
      const result = await api.get<ApiResponse<User[]>>('/users?is_active=true&limit=100');
      if (!result.success || !Array.isArray(result.data)) throw new Error(result.error || 'Benutzer konnten nicht geladen werden.');
      setAvailableUsers(result.data);
    } catch (e) {
      setAccessError(e instanceof Error ? e.message : 'Benutzer konnten nicht geladen werden.');
    } finally {
      setAccessLoading(false);
    }
  };

  const grantAccess = async () => {
    const user = availableUsers.find((row) => row.id === accessUserId);
    if (!user || accessBusy) return;
    setAccessBusy(true);
    setAccessError(null);
    try {
      const result = await api.post<ApiResponse<unknown>>(`/suppliers/${supplierId}/access`, { user_id: user.id });
      if (!result.success) throw new Error(result.error || 'Zugriff konnte nicht gewährt werden.');
      setAccessUsers((prev) => [...prev, { id: user.id, email: user.email, name: user.full_name }]);
      setAccessDialogOpen(false);
    } catch (e) {
      setAccessError(e instanceof Error ? e.message : 'Zugriff konnte nicht gewährt werden.');
    } finally {
      setAccessBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-border border-t-transparent" />
      </div>
    );
  }

  if (!supplier) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <p role="alert" className="text-danger">{error || 'Lieferant nicht gefunden.'}</p>
        <Button className="mt-4" variant="outline" onClick={() => onBack ? onBack() : onNavigate('/suppliers')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Zurück zu Lieferanten
        </Button>
      </div>
    );
  }

  const budget = supplier.budgets?.find((b) => b.year === currentYear);
  const used = budget?.used_budget ?? 0;
  const total = budget?.total_budget ?? 0;
  const pct = total > 0 ? Math.min(100, (used / total) * 100) : 0;

  const supplierActive = supplier.is_active;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => onBack ? onBack() : onNavigate('/suppliers')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Lieferanten
        </Button>
      </div>

      {!supplierActive && (
        <div
          className="rounded-lg border border-warning/30 bg-warning-soft px-4 py-3 text-sm text-warning"
          role="status"
        >
          Dieser Lieferant ist <strong className="font-semibold">inaktiv</strong>. Aktivitäten, Kampagnen und Budgets sind
          deaktiviert; der Partner fliesst nicht mehr in Auswertungen ein.
        </div>
      )}

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-card-hover text-foreground dark:bg-primary-hover dark:text-muted">
            <Building2 className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">{supplier.name}</h1>
            <p className="text-sm text-muted">{supplier.company} · {supplier.contact_email}</p>
          </div>
        </div>
        <div className="flex w-full flex-col items-stretch gap-2 sm:w-auto sm:min-w-[min(100%,20rem)] sm:items-end">
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button
              type="button"
              disabled={companionBusy || !supplierActive}
              onClick={() => {
                openCompanionDialog();
                setSupplierTab('companions');
              }}
              className="w-full sm:w-auto"
            >
              <Plus className="mr-2 h-4 w-4" />
              Companion hinzufügen
            </Button>
            <Button type="button" variant="outline" onClick={openEdit} disabled={!supplierActive} className="w-full sm:w-auto">
              <Pencil className="mr-2 h-4 w-4" />
              Bearbeiten
            </Button>
            {supplierActive && (
              <Button type="button" variant="destructive" onClick={() => void deactivateSupplier()} className="w-full sm:w-auto">
                <UserX className="mr-2 h-4 w-4" />
                Deaktivieren
              </Button>
            )}
          </div>
          <button
            type="button"
            onClick={() => onNavigate('/companion')}
            className="inline-flex items-center justify-center gap-1.5 self-end text-xs text-muted underline-offset-2 hover:text-foreground hover:underline dark:hover:text-muted"
          >
            <Bot className="h-3.5 w-3.5" />
            Zur globalen Companion-Übersicht (optional)
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-warning/30 bg-warning-soft px-4 py-3 text-sm text-warning">
          {error}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Card className="border-border bg-card shadow-sm">
          <CardContent className="pt-6">
            <p className="text-xs text-muted uppercase tracking-wide mb-1">Budget {currentYear}</p>
            <p className="text-lg font-semibold">{formatCurrency(used)} <span className="text-sm font-normal text-muted">/ {formatCurrency(total)}</span></p>
            <Progress value={pct} className="h-2 mt-2" />
            {companions.some((c) => c.year === currentYear) && (
              <p className="mt-2 text-xs text-muted">
                Obergrenze aus Companion-Zuteilungen: {formatCurrency(companionSumForYear)}
              </p>
            )}
          </CardContent>
        </Card>
        <Card className="border-border bg-card shadow-sm">
          <CardContent className="pt-6">
            <p className="text-xs text-muted uppercase tracking-wide mb-1">Kampagnen</p>
            <p className="text-lg font-semibold">{supplier.campaigns_count ?? detailCampaigns.length}</p>
          </CardContent>
        </Card>
        <Card className="border-border bg-card shadow-sm">
          <CardContent className="pt-6">
            <p className="text-xs text-muted uppercase tracking-wide mb-1">Companion</p>
            <p className="text-lg font-semibold">{companions.length}</p>
            <Button
              type="button"
              variant="link"
              className="mt-1 h-auto p-0 text-sm font-medium text-foreground dark:text-muted"
              disabled={companionBusy || !supplierActive}
              onClick={() => {
                openCompanionDialog();
                setSupplierTab('companions');
              }}
            >
              Companion hinzufügen
            </Button>
          </CardContent>
        </Card>
        <Card className="border-border bg-card shadow-sm">
          <CardContent className="pt-6">
            <p className="text-xs text-muted uppercase tracking-wide mb-1">Status</p>
            <Badge variant={supplier.is_active ? 'default' : 'secondary'}>
              {supplier.is_active ? 'Aktiv' : 'Inaktiv'}
            </Badge>
          </CardContent>
        </Card>
      </div>

      <Card className="border-border bg-card shadow-sm">
        <CardContent className="pt-6">
          <Tabs value={supplierTab} onValueChange={setSupplierTab}>
            <TabsList className="mb-4 flex h-auto min-h-10 w-full flex-wrap justify-start gap-1">
              <TabsTrigger value="info">Infos</TabsTrigger>
              <TabsTrigger value="budgets">Jahresbudgets</TabsTrigger>
              <TabsTrigger value="companions" className="gap-1">
                <Bot className="h-3.5 w-3.5" />
                Companion
              </TabsTrigger>
              <TabsTrigger value="campaigns">Kampagnen</TabsTrigger>
              <TabsTrigger value="documents">Dokumente</TabsTrigger>
              <TabsTrigger value="access">Zugriff</TabsTrigger>
            </TabsList>

            <TabsContent value="info" className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <p className="text-xs text-muted uppercase tracking-wide">Ansprechpartner</p>
                  <p className="mt-1 font-medium">{supplier.contact_name || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted uppercase tracking-wide">E-Mail</p>
                  <p className="mt-1 font-medium">{supplier.contact_email || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted uppercase tracking-wide">Telefon</p>
                  <p className="mt-1 font-medium">{supplier.phone || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted uppercase tracking-wide">Firma</p>
                  <p className="mt-1 font-medium">{supplier.company || '—'}</p>
                </div>
              </div>
              {supplier.notes && (
                <div className="mt-4 rounded-lg bg-background px-4 py-3 text-sm text-muted dark:bg-card-hover">
                  {supplier.notes}
                </div>
              )}
            </TabsContent>

            <TabsContent value="budgets" className="space-y-4">
              {companions.length > 0 && (
                <p className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-muted dark:bg-card-hover/50">
                  Das Jahresbudget wird aus der Summe der Companion-Zuteilungen je Jahr synchronisiert (Tab Companion). Verbrauch
                  (genutzt) kann weiterhin über Kampagnen und Auswertungen befüllt werden.
                </p>
              )}
              {detailBudgets.length === 0 ? (
                <p className="text-muted">Keine Budgets hinterlegt.</p>
              ) : (
                detailBudgets.map((b) => {
                  const bPct = b.total_budget > 0 ? Math.min(100, (b.used_budget / b.total_budget) * 100) : 0;
                  const compYear = companions.filter((c) => c.year === b.year);
                  const compSum = compYear.reduce((s, c) => s + (Number(c.allocated_budget) || 0), 0);
                  return (
                    <div key={b.id}>
                      <div className="mb-1 flex flex-wrap justify-between gap-2 text-sm">
                        <span className="font-medium">{b.year}</span>
                        <span className="text-muted">{formatCurrency(b.used_budget)} / {formatCurrency(b.total_budget)}</span>
                      </div>
                      <Progress value={bPct} className="h-2" />
                      {compYear.length > 0 && (
                        <p className="mt-1 text-xs text-muted">
                          Companion-Zuteilungen {b.year}: {formatCurrency(compSum)} ({compYear.length} Einträge)
                        </p>
                      )}
                    </div>
                  );
                })
              )}
            </TabsContent>

            <TabsContent value="companions" className="space-y-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-muted">
                  Jeder Companion ist ein KI-Agent für diesen Lieferanten. Budget-Anteile pro Jahr fliessen in das Jahresbudget ein.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={companionBusy || !supplierActive}
                    onClick={() => onNavigate('/companion')}
                  >
                    Companion-Übersicht
                  </Button>
                  <Button
                    size="sm"
                    disabled={companionBusy || !supplierActive}
                    onClick={() => {
                      openCompanionDialog();
                    }}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Companion hinzufügen
                  </Button>
                </div>
              </div>
              {companions.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border bg-background/50 px-6 py-10 text-center dark:bg-card/30">
                  <Bot className="mx-auto h-10 w-10 text-muted" aria-hidden />
                  <p className="mt-3 font-medium text-foreground">Noch kein Companion für diesen Lieferanten</p>
                  <p className="mt-1 text-sm text-muted">
                    Hier können Sie einen KI-Agenten anlegen und budgetieren – ohne zur Companion-Seite zu wechseln.
                  </p>
                  <Button type="button" className="mt-5" disabled={companionBusy || !supplierActive} onClick={() => openCompanionDialog()}>
                    <Plus className="mr-2 h-4 w-4" />
                    Companion hinzufügen
                  </Button>
                </div>
              ) : (
                <div className="w-full space-y-2">
                  {companions.map((row) => {
                    const agent = row.ai_agents;
                    const label = agent?.name ?? 'Companion';
                    return (
                      <div
                        key={row.id}
                        className="flex w-full flex-col gap-3 rounded-lg border border-border px-3 py-3 sm:flex-row sm:items-center sm:justify-between"
                      >
                        <div className="min-w-0 flex items-start gap-2">
                          <Bot className="mt-0.5 h-4 w-4 shrink-0 text-foreground" />
                          <div className="min-w-0">
                            <p className="font-medium truncate">{label}</p>
                            <p className="text-xs text-muted">
                              {agent?.provider ?? '—'} · {agent?.model ?? '—'} · Jahr {row.year}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                          <Button
                            type="button"
                            size="sm"
                            className="shrink-0"
                            disabled={!supplierActive || !row.ai_agent_id}
                            onClick={() => {
                              if (!row.ai_agent_id) return;
                              onNavigate(`/companion?agent=${encodeURIComponent(row.ai_agent_id)}`);
                            }}
                          >
                            <MessageSquare className="mr-2 h-4 w-4" />
                            Zum Chat
                          </Button>
                          <Label htmlFor={`alloc-${row.id}`} className="sr-only">
                            Zuteilung CHF
                          </Label>
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-muted">CHF</span>
                            <Input
                              id={`alloc-${row.id}`}
                              className="h-9 w-32"
                              inputMode="decimal"
                              disabled={companionBusy || !supplierActive}
                              value={allocDrafts[row.id] ?? String(row.allocated_budget)}
                              onChange={(e) =>
                                setAllocDrafts((d) => ({ ...d, [row.id]: e.target.value }))
                              }
                              onBlur={() => void saveCompanionAllocation(row)}
                            />
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={companionBusy || !supplierActive}
                            onClick={() => removeCompanion(row.ai_agent_id)}
                          >
                            <Trash2 className="h-4 w-4 text-muted" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            <TabsContent value="campaigns" className="space-y-2">
              <div className="flex w-full flex-col gap-2 rounded-lg border border-border bg-background/90 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between dark:bg-card-hover/60">
                <p className="min-w-0 text-sm text-muted">
                  Neue Kampagne mit diesem Lieferanten anlegen? Öffnet die Kampagnen-Seite mit vorausgewähltem Lieferanten.
                </p>
                <Button
                  type="button"
                  size="sm"
                  className="shrink-0"
                  disabled={!supplierActive}
                  onClick={() => {
                    window.localStorage.setItem('leftclick_open_create_campaign', '1');
                    window.localStorage.setItem('leftclick_create_campaign_supplier_id', supplierId);
                    onNavigate('/campaigns');
                  }}
                >
                  <Megaphone className="mr-2 h-4 w-4" />
                  Neue Kampagne
                </Button>
              </div>
              <div className="flex flex-col gap-2 rounded-lg border border-border bg-background/90 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between dark:bg-card-hover/60">
                <p className="text-sm text-muted">
                  KI-Profil für diesen Lieferanten anlegen? Das geht hier direkt – ohne zur Companion-Seite zu wechseln.
                </p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="shrink-0"
                  disabled={companionBusy || !supplierActive}
                  onClick={() => {
                    openCompanionDialog();
                    setSupplierTab('companions');
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Companion hinzufügen
                </Button>
              </div>
              {detailCampaigns.length === 0 ? (
                <div className="w-full rounded-xl border border-dashed border-border bg-background/50 px-6 py-10 text-center dark:bg-card/30">
                  <Megaphone className="mx-auto h-10 w-10 text-muted" aria-hidden />
                  <p className="mt-3 font-medium text-foreground">Noch keine Kampagnen für diesen Lieferanten</p>
                  <p className="mt-1 text-sm text-muted">
                    Kampagnen werden unter «Kampagnen» angelegt und hier mit diesem Lieferanten verknüpft.
                  </p>
                  <Button
                    type="button"
                    className="mt-5"
                    disabled={!supplierActive}
                    onClick={() => {
                      window.localStorage.setItem('leftclick_open_create_campaign', '1');
                      window.localStorage.setItem('leftclick_create_campaign_supplier_id', supplierId);
                      onNavigate('/campaigns');
                    }}
                  >
                    <Megaphone className="mr-2 h-4 w-4" />
                    Neue Kampagne
                  </Button>
                </div>
              ) : (
                detailCampaigns.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className="w-full flex items-center justify-between rounded-lg border border-border px-3 py-2.5 text-left transition-colors hover:bg-background dark:hover:bg-card-hover cursor-pointer"
                    onClick={() =>
                      onNavigate(withReturnTo(`/campaigns/${encodeURIComponent(c.id)}`, `/suppliers/${supplierId}`))
                    }
                  >
                    <span className="font-medium">{c.name}</span>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary">{c.status}</Badge>
                      <ExternalLink className="h-3.5 w-3.5 text-muted" />
                    </div>
                  </button>
                ))
              )}
            </TabsContent>

            <TabsContent value="documents" className="space-y-2">
              {detailDocs.length === 0 ? (
                <p className="text-muted">Keine geteilten Dokumente.</p>
              ) : (
                detailDocs.map((d) => (
                  <div key={d.id} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2">
                    <FileText className="h-4 w-4 text-muted" />
                    <span className="text-sm">{d.name}</span>
                  </div>
                ))
              )}
            </TabsContent>

            <TabsContent value="access" className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-muted">Portal-Zugriff für Lieferant verwalten</p>
                <Button size="sm" variant="outline" disabled={!supplierActive || accessBusy} onClick={() => void openAccessDialog()}>
                  <UserPlus className="mr-2 h-4 w-4" />
                  Zugriff gewähren
                </Button>
              </div>
              <ScrollArea className="h-[200px] rounded-md border border-border">
                <ul className="p-2 space-y-1">
                  {accessUsers.length === 0 ? (
                    <li className="py-4 text-center text-sm text-muted">Keine Benutzer mit Zugriff.</li>
                  ) : (
                    accessUsers.map((u) => (
                      <li key={u.id} className="flex items-center justify-between rounded px-2 py-2 hover:bg-background dark:hover:bg-card-hover">
                        <span className="text-sm">{u.name} · {u.email}</span>
                        <Button size="sm" variant="ghost" aria-label={`Zugriff für ${u.name} entziehen`} disabled={!supplierActive || accessBusy} onClick={() => void revokeAccess(u.id)}>
                          <UserMinus className="h-4 w-4 text-danger" />
                        </Button>
                      </li>
                    ))
                  )}
                </ul>
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <Dialog open={accessDialogOpen} onOpenChange={(open) => { if (!accessBusy) setAccessDialogOpen(open); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Portal-Zugriff gewähren</DialogTitle></DialogHeader>
          {accessError && <p role="alert" className="text-sm text-danger">{accessError}</p>}
          <div className="space-y-2">
            <Label htmlFor="supplier-access-user">Benutzer</Label>
            <select
              id="supplier-access-user"
              className="h-10 w-full rounded-md border border-border bg-card px-3 text-sm"
              value={accessUserId}
              disabled={accessLoading || accessBusy}
              onChange={(event) => setAccessUserId(event.target.value)}
            >
              <option value="">{accessLoading ? 'Wird geladen…' : 'Benutzer wählen'}</option>
              {availableUsers.filter((user) => !accessUsers.some((row) => row.id === user.id)).map((user) => (
                <option key={user.id} value={user.id}>{user.full_name} · {user.email}</option>
              ))}
            </select>
            {!accessLoading && !accessError && availableUsers.every((user) => accessUsers.some((row) => row.id === user.id)) && (
              <p className="text-sm text-muted">Keine weiteren aktiven Benutzer verfügbar.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={accessBusy} onClick={() => setAccessDialogOpen(false)}>Abbrechen</Button>
            <Button disabled={accessLoading || accessBusy || !accessUserId} onClick={() => void grantAccess()}>
              {accessBusy ? 'Wird gespeichert…' : 'Zugriff gewähren'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Lieferant bearbeiten</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Name</Label>
              <AIInput id="name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} onValueChange={(v) => setForm((f) => ({ ...f, name: v }))} placeholder="z. B. Media Agentur AG" aiFieldLabel="Lieferantenname" aiContext="Marketing-Lieferant" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="company">Firma</Label>
              <AIInput id="company" value={form.company} onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))} onValueChange={(v) => setForm((f) => ({ ...f, company: v }))} placeholder="Firmenname" aiFieldLabel="Firmenname" aiContext={`Lieferant: ${form.name}`} />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="contact_name">Ansprechpartner</Label>
              <Input id="contact_name" value={form.contact_name} onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))} placeholder="Name" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="contact_email">E-Mail</Label>
              <Input id="contact_email" type="email" value={form.contact_email} onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))} placeholder="email@beispiel.ch" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="phone">Telefon</Label>
              <Input id="phone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="+41 44 123 45 67" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="notes">Notizen</Label>
              <AIInput id="notes" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} onValueChange={(v) => setForm((f) => ({ ...f, notes: v }))} placeholder="Optionale Notizen" aiFieldLabel="Lieferanten-Notizen" aiContext={`Lieferant: ${form.name || form.company}`} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={saveSupplier}>Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={companionDialogOpen} onOpenChange={setCompanionDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Companion hinzufügen</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label htmlFor="comp-name">Name</Label>
              <AIInput
                id="comp-name"
                value={newCompanion.name}
                onChange={(e) => setNewCompanion((f) => ({ ...f, name: e.target.value }))}
                onValueChange={(v) => setNewCompanion((f) => ({ ...f, name: v }))}
                placeholder="z. B. Swisscom Content Companion"
                aiFieldLabel="Companion-Name"
                aiContext={
                  supplier
                    ? `KI-Companion für den Lieferanten «${supplier.name}» (Marketing-Zulieferer). Kurz und eindeutig.`
                    : 'KI-Companion für einen Marketing-Lieferanten. Kurz und eindeutig.'
                }
              />
            </div>
            <div className="grid gap-2">
              <Label>Provider</Label>
              <Select
                value={newCompanion.provider}
                onValueChange={(v) =>
                  setNewCompanion((f) => ({ ...f, provider: v as 'openai' | 'azure' | 'modelrouter' | 'ollama' }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMPANION_PROVIDERS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="comp-model">Modell</Label>
              <select
                id="comp-model-quick"
                className={cn(
                  'flex h-9 w-full rounded-md border border-border bg-card px-3 py-2 text-sm shadow-sm',
                  'focus:outline-none focus:ring-2 focus:ring-border focus:ring-offset-2',
                  'dark:bg-background dark:ring-offset-background'
                )}
                value={modelQuickPickOptions.includes(newCompanion.model) ? newCompanion.model : ''}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v) setNewCompanion((f) => ({ ...f, model: v }));
                }}
                aria-label="Modell oder Deployment aus gespeicherten Einstellungen wählen"
              >
                <option value="">Schnellauswahl aus Einstellungen …</option>
                {modelQuickPickOptions.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
              <Input
                id="comp-model"
                value={newCompanion.model}
                onChange={(e) => setNewCompanion((f) => ({ ...f, model: e.target.value }))}
                placeholder="gpt-4o"
                autoComplete="off"
                className={cn('font-mono text-sm')}
              />
              <p className="text-[11px] leading-snug text-muted">{COMPANION_MODEL_FIELD_HINT}</p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="comp-year">Jahr</Label>
              <Input
                id="comp-year"
                type="number"
                min={2000}
                max={2100}
                value={newCompanion.year}
                onChange={(e) =>
                  setNewCompanion((f) => ({ ...f, year: Number.parseInt(e.target.value, 10) || currentYear }))
                }
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="comp-budget">Budget-Zuteilung (CHF)</Label>
              <Input
                id="comp-budget"
                inputMode="decimal"
                value={newCompanion.allocated_budget}
                onChange={(e) => setNewCompanion((f) => ({ ...f, allocated_budget: e.target.value }))}
                placeholder="0"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompanionDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={() => void createCompanion()} disabled={companionBusy || !supplierActive || !newCompanion.name.trim()}>
              {companionBusy ? 'Wird gespeichert…' : 'Anlegen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
